import { X, Layers, Trash2 } from "lucide-react";
import { FIELD_TYPES, ACCENT } from "../../../../constants/formflow/fieldTypes";
import { roleColor } from "../../../../utils/formflow/helpers";

function FieldListItem({ field, roles, isSelected, onClick }) {
  const c = roleColor(roles, field.roleId);
  const ft = FIELD_TYPES.find((t) => t.id === field.type);
  return (
    <div onClick={onClick} className={`flex items-center gap-1.5 py-1.5 px-2 rounded-md cursor-pointer ${isSelected ? "bg-gray-100" : "bg-transparent"}`}>
      <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: c.dot }} />
      {ft && <ft.Icon size={12} color="#9ca3af" />}
      <span className="text-xs text-gray-700 flex-1 overflow-hidden text-ellipsis whitespace-nowrap">{field.label}</span>
      {field.required ? <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: ACCENT }} /> : ""}
    </div>
  );
}

// ── Reusable label ────────────────────────────────────────────────────────────
function PropLabel({ children }) {
  return <label className="block text-[11px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">{children}</label>;
}

// ── Small text input ──────────────────────────────────────────────────────────
function PropInput({ type = "text", value, onChange, min, max, placeholder }) {
  return <input type={type} value={value} onChange={onChange} min={min} max={max} placeholder={placeholder} className="w-full px-2.5 py-2 border border-gray-200 rounded-md text-sm outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 box-border" />;
}

export function BuilderRightPanel({ selectedField, form, updateField, deleteField, setSelField, selField }) {
  // ── Types that show placeholder ───────────────────────────────────────────
  const hasPlaceholder = !["checkbox", "signature", "initials"].includes(selectedField?.type);

  // ── Field type options (same list as your left panel) ────────────────────
  const typeOptions = FIELD_TYPES.map((ft) => ({
    value: ft.id,
    label: ft.label,
  }));

  // ── Empty state ───────────────────────────────────────────────────────────
  if (!selectedField) {
    return (
      <div className="w-64 bg-white border-l border-gray-200 flex flex-col overflow-hidden shrink-0">
        <div className="flex-1 flex flex-col items-center justify-center text-center text-gray-400 p-6">
          <Layers size={30} className="text-gray-300 mb-2.5" />
          <p className="m-0 text-sm font-medium text-gray-500">Select a field to edit</p>
          <p className="m-0 text-xs">Click any placed field to see properties</p>
        </div>

        {form.fields?.length > 0 && (
          <div className="border-t border-gray-200 py-3 px-3.5 max-h-55 overflow-y-auto">
            <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-2">All Fields ({form.fields.length})</div>
            <div className="flex flex-col gap-0.5">
              {form.fields.map((field) => (
                <FieldListItem key={field.id} field={field} roles={form.roles} isSelected={selField === field.id} onClick={() => setSelField(field.id)} />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="w-64 bg-white border-l border-gray-200 flex flex-col overflow-y-auto">
      <div className="p-4 h-full">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="m-0 text-sm font-bold text-gray-900">Field Properties</h3>
          <button onClick={() => setSelField(null)} className="bg-none border-none cursor-pointer text-gray-400 p-1 rounded hover:bg-gray-100">
            <X size={15} />
          </button>
        </div>

        <div className="flex flex-col gap-3.5 h-full">
          {/* ── Field Type ─────────────────────────────────────────────────── */}
          <div>
            <PropLabel>Field Type</PropLabel>
            <select
              value={selectedField.type}
              onChange={(e) => {
                const newType = e.target.value;
                // Reset limit + placeholder when type changes
                updateField(selectedField.id, {
                  type: newType,
                  maxLength: null,
                  placeholder: "",
                });
              }}
              className="w-full px-2.5 py-2 border border-gray-200 rounded-md text-sm outline-none bg-white cursor-pointer focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
            >
              {typeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* ── Label ──────────────────────────────────────────────────────── */}
          <div>
            <PropLabel>Label</PropLabel>
            <PropInput value={selectedField.label} onChange={(e) => updateField(selectedField.id, { label: e.target.value })} />
          </div>

          {/* ── Assigned Role ──────────────────────────────────────────────── */}
          <div>
            <PropLabel>Assigned Role</PropLabel>
            <select value={selectedField.roleId} onChange={(e) => updateField(selectedField.id, { roleId: e.target.value })} className="w-full px-2.5 py-2 border border-gray-200 rounded-md text-sm outline-none bg-white cursor-pointer focus:border-blue-400 focus:ring-1 focus:ring-blue-400">
              {form.roles?.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>

          {/* ── Placeholder ────────────────────────────────────────────────── */}
          {hasPlaceholder && (
            <div>
              <PropLabel>Placeholder</PropLabel>
              <PropInput value={selectedField.placeholder || ""} onChange={(e) => updateField(selectedField.id, { placeholder: e.target.value })} placeholder="e.g. Enter your name" />
            </div>
          )}

          {/* ── Character / Digit limit ────────────────────────────────────── */}
          {/* {hasLimit && (
            <div>
              <PropLabel>{isNumberType ? "Max Digits" : "Max Characters"}</PropLabel>
              <div className="flex gap-1.5 items-center">
                <PropInput
                  type="number"
                  min={1}
                  max={isNumberType ? 20 : 500}
                  value={selectedField.maxLength ?? ""}
                  onChange={(e) => {
                    const val = e.target.value === "" ? null : parseInt(e.target.value, 10);
                    updateField(selectedField.id, { maxLength: val });
                  }}
                  placeholder={isNumberType ? "e.g. 2" : "e.g. 100"}
                />
                {selectedField.maxLength && (
                  <button onClick={() => updateField(selectedField.id, { maxLength: null })} className="shrink-0 text-gray-400 hover:text-gray-600 text-xs px-1" title="Clear limit">
                    <X size={13} />
                  </button>
                )}
              </div>

              // Visual hint showing what the limit looks like 
              {selectedField.maxLength && (
                <div className="mt-1.5 flex items-center gap-1 flex-wrap">
                  {isNumberType ? (
                    // Show digit boxes for number type
                    <div className="flex gap-0.5">
                      {Array.from({ length: selectedField.maxLength }).map((_, i) => (
                        <div key={i} className="w-4 h-5 border border-dashed border-gray-300 rounded-sm bg-gray-50 text-[9px] text-gray-300 flex items-center justify-center">
                          _
                        </div>
                      ))}
                    </div>
                  ) : (
                    // Show character count badge for text
                    <span className="text-[11px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">max {selectedField.maxLength} chars</span>
                  )}
                </div>
              )}
            </div>
          )} */}

          {/* ── Font Size (auto-detected, editable) ───────────────────────── */}
          {selectedField.fontSize != null && (
            <div>
              <PropLabel>Font Size (pt)</PropLabel>
              <PropInput type="number" min={6} max={72} value={selectedField.fontSize} onChange={(e) => updateField(selectedField.id, { fontSize: parseInt(e.target.value, 10) || 12 })} />
              <p className="text-[10px] text-gray-400 mt-1">Auto-detected from PDF · adjust if needed</p>
            </div>
          )}

          {/* ── Required toggle ─────────────────────────────────────────────── */}
          <div className="flex items-center justify-between py-2.5 px-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-700 font-medium">Required</span>
            <button onClick={() => updateField(selectedField.id, { required: !selectedField.required })} className="w-11 h-6 rounded-full border-none cursor-pointer relative transition-colors duration-200" style={{ background: selectedField.required ? ACCENT : "#d1d5db" }}>
              <div className="w-4.5 h-4.5 rounded-full bg-white absolute top-0.75 transition-all duration-200" style={{ left: selectedField.required ? 23 : 3 }} />
            </button>
          </div>

          {/* ── Hidden until signed toggle ─────────────────────────────────── */}
          <div className="flex items-center justify-between py-2.5 px-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-700 font-medium">Hidden until signed</span>
            <button onClick={() => updateField(selectedField.id, { hiddenUntilSigned: !selectedField.hiddenUntilSigned })} className="w-11 h-6 rounded-full border-none cursor-pointer relative transition-colors duration-200" style={{ background: selectedField.hiddenUntilSigned ? ACCENT : "#d1d5db" }}>
              <div className="w-4.5 h-4.5 rounded-full bg-white absolute top-0.75 transition-all duration-200" style={{ left: selectedField.hiddenUntilSigned ? 23 : 3 }} />
            </button>
          </div>

          {/* ── Position ────────────────────────────────────────────────────── */}
          <div>
            <PropLabel>Position</PropLabel>
            <div className="grid grid-cols-2 gap-1.5">
              {[
                { label: "X", key: "x" },
                { label: "Y", key: "y" },
              ].map(({ label, key }) => (
                <div key={key}>
                  <div className="text-[10px] text-gray-400 mb-0.5">{label}</div>
                  <input type="number" value={Number(selectedField[key]).toFixed(2)} onChange={(e) => updateField(selectedField.id, { [key]: +e.target.value })} className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs outline-none box-border focus:border-blue-400" />
                </div>
              ))}
            </div>
          </div>

          {/* ── Size ────────────────────────────────────────────────────────── */}
          <div>
            <PropLabel>Size</PropLabel>
            <div className="grid grid-cols-2 gap-1.5">
              {[
                { label: "W", key: "w" },
                { label: "H", key: "h" },
              ].map(({ label, key }) => (
                <div key={key}>
                  <div className="text-[10px] text-gray-400 mb-0.5">{label}</div>
                  <input type="number" value={Math.round(selectedField[key])} onChange={(e) => updateField(selectedField.id, { [key]: +e.target.value })} className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs outline-none box-border focus:border-blue-400" />
                </div>
              ))}
            </div>
          </div>

          {/* ── Page ────────────────────────────────────────────────────────── */}
          <div className="text-[11px] text-gray-400 text-center">Page {(selectedField.pageIndex ?? 0) + 1}</div>

          {/* ── Delete ──────────────────────────────────────────────────────── */}
          <button onClick={() => deleteField(selectedField.id)} className="flex items-center justify-center gap-1.5 py-2 rounded-lg border border-red-100 bg-red-50 cursor-pointer text-red-500 text-sm font-medium hover:bg-red-100 transition-colors">
            <Trash2 size={14} /> Delete Field
          </button>
        </div>
      </div>

      {/* ── Field list at bottom ─────────────────────────────────────────────── */}
      {form.fields?.length > 0 && (
        <div className="border-t border-gray-200 py-3 px-3.5 h-full">
          <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-2">All Fields ({form.fields.length})</div>
          <div className="flex flex-col gap-0.5">
            {form.fields.map((field) => (
              <FieldListItem key={field.id} field={field} roles={form.roles} isSelected={selField === field.id} onClick={() => setSelField(field.id)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
